import {
  createHotelAccessCodeSchema,
  hotelAccessCodeCrudConfig,
  type HotelAccessCode,
} from '@/config/objects/hotel-access-codes';
import {
  Button,
  CrudFormView,
  FormPageLayout,
  toast,
  type CrudConfig,
  type CrudFormViewRef,
} from '@startupquickstart/frontend';
import { ArrowLeft, Save } from 'lucide-react';
import { useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

/**
 * Hotel-scoped create-access-code form at `/hotels/:id/access-codes/new`.
 *
 * Rendered as a full page (matching the offer/amenity hotel forms) and posts to
 * the hotel-scoped `POST /hotels/:id/access-codes` endpoint, which sets the
 * hotel from the route — so the hotel picker is dropped from the form. Hotel-org
 * owners can create here (the endpoint is `requireHotelOwner`); editing remains
 * internal-only via the global form. Returns to the Access Codes tab on success
 * or cancel.
 */
export function HotelAccessCodeCreatePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const formRef = useRef<CrudFormViewRef>(null);

  const backToList = () => navigate(`/hotels/${id}/access-codes`);

  // Target the hotel-scoped create endpoint and drop the `hotelId` field/schema
  // requirement — the hotel is implied by the route.
  const config = useMemo<CrudConfig<HotelAccessCode>>(
    () => ({
      ...hotelAccessCodeCrudConfig,
      endpoints: {
        ...hotelAccessCodeCrudConfig.endpoints,
        create: `/hotels/${id}/access-codes`,
      },
      form: {
        ...hotelAccessCodeCrudConfig.form,
        // Hotel comes from the route; the code is generated server-side. Neither
        // is entered here, so drop both from the schema and the rendered fields.
        createSchema: createHotelAccessCodeSchema.omit({ hotelId: true, code: true }),
        sections: hotelAccessCodeCrudConfig.form.sections?.map(section => ({
          ...section,
          fields: section.fields.filter(field => field !== 'hotelId' && field !== 'code'),
        })),
      },
    }),
    [id],
  );

  const handleSuccess = () => {
    toast.success('Access code created for hotel');
    backToList();
  };

  const handleSave = () => formRef.current?.submit();

  if (!id) return null;

  const actions = (
    <>
      <Button type="button" variant="ghost" onClick={backToList}>
        Cancel
      </Button>
      <Button type="button" onClick={handleSave} loading={formRef.current?.isPending} icon={Save}>
        Create Access Code
      </Button>
    </>
  );

  return (
    <FormPageLayout>
      <FormPageLayout.Header>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={backToList}
            className="flex items-center justify-center h-9 w-9 rounded-lg border bg-card hover:bg-muted transition-colors cursor-pointer"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-zinc-900">Create Access Code</h1>
            <p className="text-sm text-muted-foreground">Add a new access code for this hotel</p>
          </div>
        </div>
        <div className="flex items-center gap-3">{actions}</div>
      </FormPageLayout.Header>

      <FormPageLayout.Content>
        <CrudFormView
          ref={formRef}
          config={config}
          onSuccess={handleSuccess}
          renderHeader={false}
          renderFooter={false}
        />
      </FormPageLayout.Content>

      <FormPageLayout.Footer lastSaved={formRef.current?.lastSaved}>
        <div className="flex items-center gap-3">{actions}</div>
      </FormPageLayout.Footer>
    </FormPageLayout>
  );
}
